import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useTelegram } from '../hooks/useTelegram';
import { useNavigate } from 'react-router-dom';

// Create context
const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTrade, setActiveTrade] = useState(null); // Состояние активной ручной сделки
  const navigate = useNavigate();

  // Get references to the hooks
  const { getUserData, validateTelegramData, verifyToken } = useApi();
  const { user: telegramUser, isReady: isTelegramReady, tgWebApp } = useTelegram();

  // Функция для обработки ошибок аутентификации
  const handleAuthError = useCallback((errorMessage) => {
    console.error(`Authentication error: ${errorMessage}`);
    setError(errorMessage || 'Authentication failed');
    setIsAuthenticated(false);

    // Перенаправление на страницу "без доступа" только в продакшене и только при критических ошибках
    if (process.env.NODE_ENV !== 'development') {
      console.log('Authentication failed in production, redirecting to unauthorized page');
      navigate('/unauthorized', { replace: true });
    } else {
      console.warn('⚠️ Authentication error in development mode, continuing anyway');
    }
  }, [navigate]);

  // Load user data once the component has mounted and hooks are safe to use
  useEffect(() => {
    if (!initialized && isTelegramReady) {
      const loadInitialData = async () => {
        try {
          setLoading(true);
          let authSuccess = false;
          let userData = null;

          // Проверяем токен в куках
          try {
            const tokenVerification = await verifyToken();
            if (tokenVerification.success) {
              console.log('✅ JWT token is valid, loading user data...');
              
              // Если токен валиден, загружаем данные пользователя
              userData = await getUserData();
              console.log('✅ User data loaded successfully');
              authSuccess = true;
            }
          } catch (tokenError) {
            // Если ошибка 401 - значит токен недействителен или отсутствует
            if (tokenError.response && tokenError.response.status === 401) {
              console.log('⚠️ Token verification failed with 401, trying Telegram validation...');
            } else {
              console.error('Error during token verification:', tokenError);
            }
          }

          // Если токена нет или он невалиден, но есть initData Telegram
          if (!authSuccess) {
            const hasTelegramData = Boolean(tgWebApp?.initData);
            if (hasTelegramData) {
              try {
                // Валидируем данные Telegram и получаем токен
                const validationResult = await validateTelegramData();
                if (validationResult.success) {
                  console.log('✅ Telegram authentication successful, loading user data...');

                  // Если валидация успешна, загружаем данные пользователя
                  userData = await getUserData();
                  console.log('✅ User data loaded from Telegram validation');
                  authSuccess = true;
                } else {
                  throw new Error(validationResult.message || 'Telegram validation failed');
                }
              } catch (validationError) {
                console.warn('⚠️ Telegram validation failed:', validationError.message);
                
                // В режиме разработки не обрабатываем как критическую ошибку
                if (process.env.NODE_ENV !== 'development') {
                  // Обрабатываем ошибку валидации Telegram данных
                  if (validationError.response && validationError.response.status === 401) {
                    handleAuthError('Telegram authentication failed: Invalid or expired data');
                  } else {
                    handleAuthError(validationError.message || 'Telegram validation failed');
                  }
                  return;
                }
              }
            }
          }

          // В режиме разработки продолжаем даже без токена и без initData
          if (!authSuccess && process.env.NODE_ENV === 'development') {
            console.warn('🔧 Development mode: proceeding without authentication');
            try {
              userData = await getUserData();
              authSuccess = true;
              console.log('✅ Development user data loaded');

            } catch (err) {
              console.error('❌ Error fetching development user data:', err);
              setError('Failed to load development user data');
            }
          }

          // Устанавливаем состояние только один раз в конце
          if (authSuccess && userData) {
            setUser(userData);
            setIsAuthenticated(true);
            setError(null);
          } else if (process.env.NODE_ENV !== 'development') {
            // В продакшене без токена и initData - ошибка аутентификации
            handleAuthError('Authentication required');
          }

        } catch (err) {
          console.error('❌ Error in authentication flow:', err);
          handleAuthError('Authentication failed');
        } finally {
          setLoading(false);
          setInitialized(true);
        }
      };

      loadInitialData();
    }
  }, [initialized, getUserData, validateTelegramData, isTelegramReady, verifyToken, tgWebApp, navigate]);

  const refreshUserData = useCallback(async () => {
    try {
      // Убираем setLoading(true) чтобы избежать мерцания и перерендеров
      const userData = await getUserData();
      setUser(userData);
      setError(null);
      console.log('✅ User data refreshed successfully');
    } catch (err) {
      console.error('❌ Error refreshing user data:', err);
      setError('Failed to refresh user data');

      // Если ошибка 401, обрабатываем как ошибку аутентификации только в продакшене
      if (err.response && err.response.status === 401 && process.env.NODE_ENV !== 'development') {
        handleAuthError('Session expired');
      }
    }
  }, [getUserData, handleAuthError]);

  // Функции для управления активной сделкой
  const setActiveManualTrade = useCallback((tradeData) => {
    setActiveTrade(tradeData);
    // Сохраняем в localStorage для восстановления после перезагрузки
    if (tradeData) {
      localStorage.setItem('arbifox_active_trade', JSON.stringify(tradeData));
    } else {
      localStorage.removeItem('arbifox_active_trade');
    }
  }, []);

  const clearActiveTrade = useCallback(() => {
    setActiveTrade(null);
    localStorage.removeItem('arbifox_active_trade');
  }, []);

  // Восстанавливаем активную сделку из localStorage при инициализации
  useEffect(() => {
    if (initialized && isAuthenticated) {
      try {
        const savedTrade = localStorage.getItem('arbifox_active_trade');
        if (savedTrade) {
          const tradeData = JSON.parse(savedTrade);
          
          // Проверяем, не устарела ли завершенная сделка (автоочистка через 5 минут)
          if (tradeData.stage === 'COMPLETED' && tradeData.completedTime) {
            const timeSinceCompletion = Date.now() - tradeData.completedTime;
            if (timeSinceCompletion > 5 * 60 * 1000) { // 5 минут
              console.log('🧹 Clearing old completed trade');
              localStorage.removeItem('arbifox_active_trade');
              return;
            }
          }
          
          // Проверяем, не зависла ли сделка в выполнении (автоочистка через 10 минут)
          if (tradeData.stage === 'EXECUTION' && tradeData.startTime) {
            const timeSinceStart = Date.now() - tradeData.startTime;
            if (timeSinceStart > 10 * 60 * 1000) { // 10 минут
              console.log('🧹 Clearing stuck trade execution');
              localStorage.removeItem('arbifox_active_trade');
              return;
            }
          }
          
          setActiveTrade(tradeData);
          console.log('✅ Active trade restored from localStorage');
        }
      } catch (err) {
        console.error('❌ Error restoring active trade:', err);
        localStorage.removeItem('arbifox_active_trade');
      }
    }
  }, [initialized, isAuthenticated]);

  // Функция для выхода из системы
  const logout = useCallback(() => {
    // Очищаем локальное хранилище
    localStorage.removeItem('arbifox_token');
    localStorage.removeItem('arbifox_active_trade');
    // Сбрасываем состояние
    setUser(null);
    setIsAuthenticated(false);
    setActiveTrade(null);
    // Перенаправляем на страницу "без доступа" только в продакшене
    if (process.env.NODE_ENV !== 'development') {
      console.log('Logging out, redirecting to unauthorized page');
      navigate('/unauthorized', { replace: true });
    }
  }, [navigate]);

  const value = {
    user,
    loading,
    error,
    refreshUserData,
    initialized,
    isAuthenticated,
    logout,
    activeTrade,
    setActiveManualTrade,
    clearActiveTrade
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// Custom hook to use the UserContext
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 